import { useEffect } from 'react';
import { db } from '../lib/db';
import { getBackupSettings, runFolderBackup } from '../lib/backup';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Runs automatic folder backups per the user's cadence. A single 60s tick keeps
 * it simple and avoids competing with the sync engine's write listener:
 *  - `daily`  → back up once the last backup is >24h old.
 *  - `change` → back up when a document changed since the last backup.
 * Automatic runs never prompt for permission (no user gesture); if the folder
 * permission has lapsed they no-op until the user hits "Back up now".
 */
export function useBackupScheduler(): void {
  useEffect(() => {
    let cancelled = false;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      const s = await getBackupSettings();
      if (s.cadence === 'off') return;
      const last = s.lastBackupAt ?? 0;
      if (s.cadence === 'daily' && Date.now() - last < DAY_MS) return;
      if (s.cadence === 'change') {
        const newest = await db.documents.orderBy('updatedAt').last();
        if (!newest || newest.updatedAt <= last) return;
      }
      await runFolderBackup(false);
    };

    const id = window.setInterval(() => void tick(), 60_000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);
}
